import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { categoriasConfig, categoriasReglas, categoriasMap, categoriasGlobalStats, getRadioConMasCategorias, formatearNumeroCat, type CategoriaId } from '@/mocks/top10Categoria';

export default function Top10CategoriaPage() {
  const navigate = useNavigate();
  const [activeCategoria, setActiveCategoria] = useState<CategoriaId>('regional');
  const [view, setView] = useState<'categorias' | 'multi' | 'reglas'>('categorias');

  const catConfig = categoriasConfig.find(c => c.id === activeCategoria);
  const entries = categoriasMap[activeCategoria] || [];
  const podium = entries.slice(0, 3);
  const rest = entries.slice(3);
  const radioEstrella = getRadioConMasCategorias();

  const allEntries = useMemo(() => {
    const seen = new Set<string>();
    const result: { entry: (typeof entries)[0]; categorias: CategoriaId[]; catLabel: string; catColor: string }[] = [];
    for (const cat of categoriasConfig) {
      const catEntries = categoriasMap[cat.id] || [];
      for (const e of catEntries) {
        if (!seen.has(e.entityName + cat.id)) {
          seen.add(e.entityName + cat.id);
          result.push({ entry: e, categorias: e.categorias, catLabel: cat.label, catColor: cat.color });
        }
      }
    }
    return result;
  }, []);

  const multiTop = useMemo(() => {
    const withCount = new Map<string, { entry: (typeof entries)[0]; count: number; cats: CategoriaId[] }>();
    for (const cat of categoriasConfig) {
      const catEntries = categoriasMap[cat.id] || [];
      for (const e of catEntries) {
        if (!withCount.has(e.entityName) || e.categorias.length > (withCount.get(e.entityName)?.count || 0)) {
          withCount.set(e.entityName, { entry: e, count: e.categorias.length, cats: e.categorias });
        }
      }
    }
    return [...withCount.values()].sort((a, b) => b.count - a.count).slice(0, 8);
  }, []);

  const movementIcons: Record<string, string> = { up: 'ri-arrow-up-line', down: 'ri-arrow-down-line', stable: 'ri-subtract-line' };
  const movementColors: Record<string, string> = { up: 'text-emerald-600', down: 'text-red-500', stable: 'text-foreground-400' };
  const trendDots: Record<string, string> = { exploding: 'bg-rose-500', trending: 'bg-amber-500', growing: 'bg-emerald-500', active: 'bg-sky-500', stable: 'bg-foreground-300', declining: 'bg-red-400' };

  return (
    <div className="min-h-screen bg-background-50">
      {/* Hero */}
      <section className="relative w-full h-[420px] md:h-[520px] overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Grand%20concert%20arena%20with%20nine%20illuminated%20genre%20towers%2C%20each%20with%20a%20distinct%20colorful%20glow%2C%20warm%20theatrical%20spotlight%20beams%20crossing%20the%20dark%20space%2C%20dramatic%20empty%20stage%20awaiting%20performers%2C%20cinematic%20wide%20angle%20shot%2C%20elegant%20competition%20venue%20atmosphere%20with%20deep%20shadows%20and%20saturated%20accent%20colors%2C%20golden%20amber%20and%20warm%20tones%20predominating&width=1800&height=1000&seq=top10-cat-hero&orientation=landscape"
          alt="Top 10 por Categoría"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/60"></div>

        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center px-4 w-full max-w-3xl">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-500/20 backdrop-blur-sm text-amber-200 text-xs font-bold border border-amber-400/30 mb-4">
              <i className="ri-award-line text-xs"></i>
              COMPETENCIA ESPECIALIZADA
            </span>
            <h1 className="text-3xl md:text-5xl font-black text-white mb-4">Top 10 por Categoría</h1>
            <p className="text-white/70 text-sm md:text-base max-w-xl mx-auto">
              Rankings especializados por género y formato. 9 categorías, 90 posiciones, y la regla de oro:
              <strong className="text-white"> cada radio puede pertenecer a varias categorías.</strong>
            </p>
          </div>
        </div>

        {/* Stats Overlay */}
        <div className="absolute bottom-0 left-0 right-0">
          <div className="max-w-4xl mx-auto px-4 pb-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {[
                { label: 'Categorías', value: categoriasGlobalStats.totalCategorias, icon: 'ri-layout-grid-line' },
                { label: 'Entradas Rankeadas', value: categoriasGlobalStats.totalEntradasRankeadas, icon: 'ri-stack-line' },
                { label: 'Más Competida', value: categoriasGlobalStats.categoriasConMasCompetencia, icon: 'ri-fire-line' },
                { label: `${radioEstrella.entityName}`, value: `${radioEstrella.count} categorías`, icon: 'ri-star-line' },
              ].map((s, i) => (
                <div key={i} className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/10 backdrop-blur-md border border-white/10">
                  <i className={`${s.icon} text-white/70 text-sm`}></i>
                  <div>
                    <p className="text-[10px] text-white/50">{s.label}</p>
                    <p className="text-sm font-bold text-white">{s.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar + View Switcher */}
      <div className="sticky top-0 z-30 bg-background-50/95 backdrop-blur-xl border-b border-background-200/60">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 flex items-center justify-between gap-4 flex-wrap">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-1 text-xs font-semibold text-foreground-500 hover:text-foreground-700 cursor-pointer whitespace-nowrap shrink-0"
          >
            <i className="ri-arrow-left-line"></i> RFW
          </button>
          <div className="flex items-center gap-2">
            {[
              { id: 'categorias' as const, label: 'Por Categoría', icon: 'ri-layout-grid-line' },
              { id: 'multi' as const, label: 'Multi-Categoría', icon: 'ri-stack-line' },
              { id: 'reglas' as const, label: 'Reglas', icon: 'ri-scales-line' },
            ].map(v => (
              <button
                key={v.id}
                onClick={() => setView(v.id)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap cursor-pointer transition-all ${
                  view === v.id
                    ? 'bg-foreground-900 text-background-50'
                    : 'bg-background-100 text-foreground-500 hover:text-foreground-700 border border-background-200/60'
                }`}
              >
                <i className={`${v.icon} text-[10px]`}></i>
                {v.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">

        {/* VIEW: Por Categoría */}
        {view === 'categorias' && (
          <>
            {/* Categoría Tabs */}
            <div className="flex flex-wrap items-center gap-2 mb-6">
              {categoriasConfig.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategoria(cat.id)}
                  className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold whitespace-nowrap cursor-pointer transition-all ${
                    activeCategoria === cat.id
                      ? `${cat.bgLight} ${cat.color} border border-current/20`
                      : 'bg-background-100 text-foreground-500 hover:text-foreground-700 border border-background-200/60'
                  }`}
                >
                  <i className={`${cat.icon} text-[11px]`}></i>
                  {cat.label}
                </button>
              ))}
            </div>

            {/* Category Info */}
            {catConfig && (
              <div className="flex items-start gap-4 p-5 rounded-2xl bg-background-100 border border-background-200/70 mb-8">
                <div className={`w-11 h-11 rounded-xl ${catConfig.bgLight} flex items-center justify-center shrink-0`}>
                  <i className={`${catConfig.icon} ${catConfig.color} text-xl`}></i>
                </div>
                <div className="flex-1">
                  <h2 className="text-xl font-bold text-foreground-900">
                    {catConfig.label} <span className="text-foreground-400 font-normal text-sm">— {catConfig.totalRadios} radios activas</span>
                  </h2>
                  <p className="text-sm text-foreground-600 mt-1">{catConfig.description}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-xs text-foreground-500">Top 1</p>
                  <p className="text-sm font-bold text-foreground-900">{entries[0]?.entityName || '—'}</p>
                  <p className="text-xs text-foreground-500">{entries[0]?.scoreLabel || ''}</p>
                </div>
              </div>
            )}

            {/* Podium Top 3 */}
            {podium.length >= 3 && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
                {/* #2 Plata */}
                <div className="order-2 md:order-1 p-6 rounded-2xl bg-background-100 border border-background-200/70 text-center">
                  <span className="inline-flex items-center px-3 py-1 rounded-full bg-slate-100 text-slate-600 text-xs font-bold mb-4">🥈 #2</span>
                  <img src={podium[1].logoUrl} alt={podium[1].entityName} className="w-20 h-20 rounded-2xl object-cover mx-auto mb-3" loading="lazy" />
                  <h3 className="text-base font-bold text-foreground-900 truncate">{podium[1].entityName}</h3>
                  <p className="text-xs text-foreground-500 mt-0.5">{podium[1].frecuencia} · {podium[1].ciudad}</p>
                  <p className="text-lg font-black text-foreground-800 mt-2">{podium[1].scoreLabel}</p>
                  <div className="flex items-center justify-center gap-3 mt-1">
                    <span className={`text-xs font-semibold ${podium[1].scoreChange >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                      {podium[1].scoreChange >= 0 ? '+' : ''}{podium[1].scoreChange}
                    </span>
                    <span className={`text-xs flex items-center gap-0.5 ${movementColors[podium[1].movement]}`}>
                      <i className={`${movementIcons[podium[1].movement]} text-[10px]`}></i>
                      {podium[1].movementValue}
                    </span>
                  </div>
                  <div className="flex items-center justify-center gap-2 mt-2">
                    {podium[1].isLive && (
                      <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full bg-red-100 text-red-600 text-[9px] font-bold">
                        <span className="w-1 h-1 rounded-full bg-red-500 animate-pulse"></span>EN VIVO
                      </span>
                    )}
                    <span className={`w-2 h-2 rounded-full ${trendDots[podium[1].trend]}`}></span>
                  </div>
                </div>

                {/* #1 Oro */}
                <div className="order-1 md:order-2 p-6 rounded-2xl bg-amber-50 border-2 border-amber-200 text-center -mt-4 md:-mt-6">
                  <span className="inline-flex items-center px-3 py-1 rounded-full bg-amber-200 text-amber-800 text-xs font-bold mb-4">🥇 #1</span>
                  <img src={podium[0].logoUrl} alt={podium[0].entityName} className="w-24 h-24 rounded-2xl object-cover mx-auto mb-3 ring-4 ring-amber-300" loading="lazy" />
                  <h3 className="text-lg font-black text-foreground-900 truncate">{podium[0].entityName}</h3>
                  <p className="text-xs text-foreground-500 mt-0.5">{podium[0].frecuencia} · {podium[0].ciudad}</p>
                  <p className="text-xl font-black text-amber-700 mt-2">{podium[0].scoreLabel}</p>
                  <div className="flex items-center justify-center gap-3 mt-1">
                    <span className="text-sm font-bold text-emerald-600">+{podium[0].scoreChange}</span>
                    <span className={`text-xs flex items-center gap-0.5 ${movementColors[podium[0].movement]}`}>
                      <i className={`${movementIcons[podium[0].movement]} text-[10px]`}></i>
                      {podium[0].movementValue}
                    </span>
                  </div>
                  <div className="flex items-center justify-center gap-2 mt-2">
                    {podium[0].isLive && (
                      <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full bg-red-500/90 text-white text-[9px] font-bold">
                        <span className="w-1 h-1 rounded-full bg-white animate-pulse"></span>EN VIVO
                      </span>
                    )}
                    <span className={`w-2 h-2 rounded-full ${trendDots[podium[0].trend]}`}></span>
                  </div>
                  <div className="mt-3 flex flex-wrap justify-center gap-1">
                    {podium[0].categorias.slice(0, 4).map(cId => {
                      const cfg = categoriasConfig.find(cc => cc.id === cId);
                      return cfg ? (
                        <span key={cId} className={`px-1.5 py-0.5 rounded-md text-[9px] font-bold ${cfg.bgLight} ${cfg.color}`}>{cfg.label}</span>
                      ) : null;
                    })}
                    {podium[0].categorias.length > 4 && (
                      <span className="px-1.5 py-0.5 rounded-md text-[9px] font-bold bg-foreground-100 text-foreground-500">+{podium[0].categorias.length - 4}</span>
                    )}
                  </div>
                </div>

                {/* #3 Bronce */}
                <div className="order-3 p-6 rounded-2xl bg-background-100 border border-background-200/70 text-center">
                  <span className="inline-flex items-center px-3 py-1 rounded-full bg-orange-100 text-orange-700 text-xs font-bold mb-4">🥉 #3</span>
                  <img src={podium[2].logoUrl} alt={podium[2].entityName} className="w-20 h-20 rounded-2xl object-cover mx-auto mb-3" loading="lazy" />
                  <h3 className="text-base font-bold text-foreground-900 truncate">{podium[2].entityName}</h3>
                  <p className="text-xs text-foreground-500 mt-0.5">{podium[2].frecuencia} · {podium[2].ciudad}</p>
                  <p className="text-lg font-black text-foreground-800 mt-2">{podium[2].scoreLabel}</p>
                  <div className="flex items-center justify-center gap-3 mt-1">
                    <span className={`text-xs font-semibold ${podium[2].scoreChange >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                      {podium[2].scoreChange >= 0 ? '+' : ''}{podium[2].scoreChange}
                    </span>
                    <span className={`text-xs flex items-center gap-0.5 ${movementColors[podium[2].movement]}`}>
                      <i className={`${movementIcons[podium[2].movement]} text-[10px]`}></i>
                      {podium[2].movementValue}
                    </span>
                  </div>
                  <div className="flex items-center justify-center gap-2 mt-2">
                    {podium[2].isLive && (
                      <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full bg-red-100 text-red-600 text-[9px] font-bold">
                        <span className="w-1 h-1 rounded-full bg-red-500 animate-pulse"></span>EN VIVO
                      </span>
                    )}
                    <span className={`w-2 h-2 rounded-full ${trendDots[podium[2].trend]}`}></span>
                  </div>
                </div>
              </div>
            )}

            {/* Rest 4-10 */}
            <div className="space-y-2 mb-10">
              {rest.map(entry => (
                <div key={entry.entityName + entry.position} className="flex items-center gap-4 p-4 rounded-xl bg-background-100 border border-background-200/70 hover:bg-background-100/80 transition-colors">
                  <span className="w-8 h-8 rounded-full bg-foreground-100 flex items-center justify-center text-xs font-black text-foreground-600 shrink-0">
                    {entry.position}
                  </span>
                  <img src={entry.logoUrl} alt={entry.entityName} className="w-10 h-10 rounded-lg object-cover shrink-0" loading="lazy" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-bold text-foreground-900 truncate">{entry.entityName}</h3>
                      {entry.isLive && (
                        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-red-100 text-red-600 text-[9px] font-bold shrink-0">
                          <span className="w-1 h-1 rounded-full bg-red-500 animate-pulse"></span>EN VIVO
                        </span>
                      )}
                      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${trendDots[entry.trend]}`}></span>
                    </div>
                    <p className="text-xs text-foreground-500 truncate">{entry.frecuencia} · {entry.ciudad}</p>
                    <div className="flex flex-wrap items-center gap-1 mt-1">
                      {entry.categorias.slice(0, 5).map(cId => {
                        const cfg = categoriasConfig.find(cc => cc.id === cId);
                        if (!cfg) return null;
                        const isActive = cId === activeCategoria;
                        return (
                          <span key={cId} className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${isActive ? `${cfg.bgLight} ${cfg.color}` : 'bg-foreground-100 text-foreground-400'}`}>
                            {cfg.label}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-sm font-bold text-foreground-800">{entry.scoreLabel}</p>
                    <div className="flex items-center justify-end gap-2 mt-0.5">
                      <span className={`text-[11px] font-semibold ${entry.scoreChange >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                        {entry.scoreChange >= 0 ? '+' : ''}{entry.scoreChange}
                      </span>
                      <span className={`text-[11px] flex items-center gap-0.5 ${movementColors[entry.movement]}`}>
                        <i className={`${movementIcons[entry.movement]} text-[9px]`}></i>
                        {entry.movementValue}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* VIEW: Multi-Categoría */}
        {view === 'multi' && (
          <div className="mb-10">
            <h2 className="text-xl font-bold text-foreground-900 mb-2">Radios que dominan Múltiples Categorías</h2>
            <p className="text-sm text-foreground-600 mb-6">Una misma radio puede rankear en varias categorías simultáneamente. Estas son las que más categorías cubren.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              {multiTop.map(item => (
                <div key={item.entry.entityName} className="p-5 rounded-2xl bg-background-100 border border-background-200/70">
                  <div className="flex items-center gap-3 mb-3">
                    <img src={item.entry.logoUrl} alt={item.entry.entityName} className="w-12 h-12 rounded-xl object-cover" loading="lazy" />
                    <div className="min-w-0">
                      <h3 className="text-sm font-bold text-foreground-900 truncate">{item.entry.entityName}</h3>
                      <p className="text-xs text-foreground-500">{item.entry.frecuencia}</p>
                    </div>
                  </div>
                  <p className="text-2xl font-black text-foreground-800 mb-2">{item.count} <span className="text-sm font-normal text-foreground-500">categorías</span></p>
                  <div className="flex flex-wrap gap-1">
                    {item.cats.map(cId => {
                      const cfg = categoriasConfig.find(cc => cc.id === cId);
                      return cfg ? (
                        <span key={cId} className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${cfg.bgLight} ${cfg.color}`}>{cfg.label}</span>
                      ) : null;
                    })}
                  </div>
                </div>
              ))}
            </div>

            {/* All entries with category badges */}
            <h3 className="text-lg font-bold text-foreground-900 mb-4">Todas las Entradas por Categoría</h3>
            <div className="flex flex-wrap gap-2 mb-6">
              {categoriasConfig.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => { setActiveCategoria(cat.id); setView('categorias'); }}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap cursor-pointer transition-all ${cat.bgLight} ${cat.color} border border-current/10 hover:brightness-95`}
                >
                  <i className={`${cat.icon} text-[10px]`}></i>
                  Ver {cat.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* VIEW: Reglas */}
        {view === 'reglas' && (
          <div className="mb-10">
            <h2 className="text-xl font-bold text-foreground-900 mb-6">Las 4 Reglas del Sistema de Categorías</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-10">
              {categoriasReglas.map(regla => (
                <div key={regla.id} className="p-6 rounded-2xl bg-background-100 border border-background-200/70">
                  <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold text-white bg-gradient-to-r ${regla.gradiente} mb-3`}>
                    <i className={`${regla.icono} text-xs`}></i>
                    Regla #{regla.id}
                  </div>
                  <h4 className="text-base font-bold text-foreground-900 mb-2">{regla.titulo}</h4>
                  <p className="text-sm text-foreground-600 leading-relaxed">{regla.description}</p>
                </div>
              ))}
            </div>

            {/* Stats Summary */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 p-6 rounded-2xl bg-background-100 border border-background-200/70">
              {[
                { label: 'Categorías', value: categoriasGlobalStats.totalCategorias, icon: 'ri-layout-grid-line', color: 'amber' },
                { label: 'Entradas Totales', value: categoriasGlobalStats.totalEntradasRankeadas, icon: 'ri-stack-line', color: 'emerald' },
                { label: 'Más Competida', value: categoriasGlobalStats.categoriasConMasCompetencia, icon: 'ri-fire-line', color: 'rose' },
                { label: 'Radios con Más Cats', value: radioEstrella.entityName, icon: 'ri-star-line', color: 'violet' },
                { label: 'Máximo por Radio', value: `${radioEstrella.count} categorías`, icon: 'ri-award-line', color: 'teal' },
              ].map((s, i) => (
                <div key={i} className="text-center">
                  <div className={`w-10 h-10 rounded-xl bg-${s.color}-100 flex items-center justify-center mx-auto mb-2`}>
                    <i className={`${s.icon} text-${s.color}-600 text-sm`}></i>
                  </div>
                  <p className="text-xs text-foreground-500">{s.label}</p>
                  <p className="text-sm font-bold text-foreground-900">{s.value}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* CTA Triple */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6 mb-10">
          <button
            onClick={() => navigate('/top-10-global')}
            className="flex items-center justify-center gap-2 p-4 rounded-2xl bg-amber-50 border border-amber-200 text-amber-800 font-semibold text-sm hover:bg-amber-100 cursor-pointer transition-colors whitespace-nowrap"
          >
            <i className="ri-trophy-line"></i>
            Top 10 Global
          </button>
          <button
            onClick={() => navigate('/top-10-pais')}
            className="flex items-center justify-center gap-2 p-4 rounded-2xl bg-emerald-50 border border-emerald-200 text-emerald-800 font-semibold text-sm hover:bg-emerald-100 cursor-pointer transition-colors whitespace-nowrap"
          >
            <i className="ri-earth-line"></i>
            Top 10 por País
          </button>
          <button
            onClick={() => navigate('/tv-app')}
            className="flex items-center justify-center gap-2 p-4 rounded-2xl bg-violet-50 border border-violet-200 text-violet-800 font-semibold text-sm hover:bg-violet-100 cursor-pointer transition-colors whitespace-nowrap"
          >
            <i className="ri-tv-line"></i>
            Ver en TV App
          </button>
        </div>

      </div>
    </div>
  );
}